import { Injectable } from '@angular/core';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import environment from '@environments/environment';

declare global {
  interface Window {
    newrelic?: {
      noticeError(
        error: Error | string,
        customAttributes?: Record<string, unknown>,
      ): void;
      addPageAction(name: string, attributes?: Record<string, unknown>): void;
      setCustomAttribute(
        name: string,
        value: string | number | boolean | null,
      ): void;
      setPageViewName(name: string, host?: string): void;
      log?(message: string, attributes?: Record<string, unknown>): void;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class NewRelicService {
  private _initialized = false;

  init(): void {
    if (this._initialized || !environment.newRelic?.enabled) {
      return;
    }

    if (this.isReady()) {
      this._initialized = true;
      return;
    }

    const { licenseKey, applicationID, accountID, trustKey, agentID } =
      environment.newRelic;

    if (!licenseKey || !applicationID) {
      return;
    }

    try {
      const nativeWarn = console.warn.bind(console);
      const nativeError = console.error.bind(console);

      new BrowserAgent({
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: ['bam.nr-data.net'], enabled: true },
          session_trace: { enabled: true },
          jserrors: { enabled: true },
          logging: { enabled: true },
          metrics: { enabled: true },
          page_action: { enabled: true },
        } as Record<string, unknown>,
        info: {
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey,
          applicationID,
          sa: 1,
        },
        loader_config: {
          accountID,
          trustKey,
          agentID,
          licenseKey,
          applicationID,
        },
      });

      console.warn = nativeWarn;
      console.error = nativeError;
      this._initialized = true;
    } catch (error) {
      console.error('New Relic initialization failed:', error);
    }
  }

  isReady(): boolean {
    const nr = window.newrelic;
    const ready =
      !!nr &&
      typeof nr.addPageAction === 'function' &&
      typeof nr.noticeError === 'function';

    if (ready) {
      this._initialized = true;
    }

    return ready;
  }

  noticeError(
    error: Error | string,
    customAttributes?: Record<string, unknown>,
  ): void {
    if (!this.isReady()) {
      return;
    }

    try {
      window.newrelic?.noticeError(error, customAttributes);
    } catch (e) {
      console.error('New Relic error reporting failed:', e);
    }
  }

  addPageAction(name: string, attributes?: Record<string, unknown>): void {
    if (!this.isReady()) {
      return;
    }

    try {
      window.newrelic?.addPageAction(name, {
        ...attributes,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
    } catch (e) {
      console.error('New Relic page action failed:', e);
    }
  }

  setCustomAttribute(
    name: string,
    value: string | number | boolean | null,
  ): void {
    if (!this.isReady()) {
      return;
    }

    try {
      window.newrelic?.setCustomAttribute(name, value);
    } catch (e) {
      console.error('New Relic custom attribute failed:', e);
    }
  }

  setPageViewName(name: string, host?: string): void {
    if (!this.isReady()) {
      return;
    }

    try {
      window.newrelic?.setPageViewName(name, host);
    } catch (e) {
      console.error('New Relic page view name failed:', e);
    }
  }

  trackMetric(name: string, value: number, unit = 'ms'): void {
    this.addPageAction('CustomMetric', { metricName: name, value, unit });
  }

  trackEvent(name: string, properties?: Record<string, unknown>): void {
    this.addPageAction(name, properties);
  }

  trackError(error: unknown, properties?: Record<string, unknown>): void {
    const err =
      error instanceof Error
        ? error
        : new Error(
            typeof error === 'object' &&
              error !== null &&
              'message' in error &&
              typeof (error as { message: unknown }).message === 'string'
              ? (error as { message: string }).message
              : String(error),
          );
    this.noticeError(err, properties);
  }

  trackTrace(message: string, properties?: Record<string, unknown>): void {
    this.addPageAction('Trace', { message, ...properties });
  }

  setUserId(userId: string): void {
    this.setCustomAttribute('userId', userId);
    this.setCustomAttribute('enduser.id', userId);
  }

  setUserInfo(userInfo: {
    userId: string;
    email?: string;
    name?: string;
    role?: string;
  }): void {
    if (userInfo.userId) {
      this.setUserId(userInfo.userId);
    }
    if (userInfo.email) {
      this.setCustomAttribute('userEmail', userInfo.email);
    }
    if (userInfo.name) {
      this.setCustomAttribute('userName', userInfo.name);
    }
    if (userInfo.role) {
      this.setCustomAttribute('userRole', userInfo.role);
    }
  }

  setApplicationVersion(version: string): void {
    this.setCustomAttribute('applicationVersion', version);
    this.setCustomAttribute('release', version);
  }
}
